import range from 'lodash/range';
import { jsx } from 'panel';
import { SvgIconEl } from 'common/components/svg-icon';
import { TooltipEl } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { handleKeyPress } from 'common/util/focusable';
import { SidenavItemEl } from './sidenav-item';
import { SIDENAV_DEFAULT_WIDTH } from './constants';

function Loader() {
  return jsx("div", {
    sel: '.loader'
  }, jsx("div", {
    sel: '.mp-skeleton-header'
  }), range(9).map(_ => jsx("div", {
    sel: '.mp-skeleton-large'
  })));
}

function SidenavSectionItemsWrapper(_ref, sectionItemsChildren) {
  let {
    isExpanded,
    section
  } = _ref;

  if (isExpanded) {
    if (!section.items.length) {
      return jsx("div", {
        sel: '.empty-notice'
      }, section.emptyNotice || "This section is empty");
    }

    return jsx("div", {
      sel: '.section-items-list'
    }, sectionItemsChildren);
  }

  return null;
}

function SidenavSection(_ref2, sectionItemsChildren) {
  let {
    section,
    isExpanded,
    sectionDragDisabled,
    onToggleSection
  } = _ref2;
  return jsx("div", {
    sel: '.section',
    class: {
      'active-dragging-disabled': sectionDragDisabled
    }
  }, jsx("div", {
    sel: '.section-header',
    attrs: {
      tabindex: 0
    },
    on: {
      click: () => onToggleSection(section.label),
      keypress: handleKeyPress
    }
  }, jsx(SvgIconEl, {
    sel: '.caret',
    attrs: {
      icon: isExpanded ? "caret-down" : "caret-right"
    }
  }), jsx("div", {
    sel: '.section-header-label'
  }, section.label)), jsx(SidenavSectionItemsWrapper, {
    isExpanded: isExpanded,
    section: section
  }, sectionItemsChildren));
}

export function template() {
  const allSelectedIndices = this.helpers.getAllSelectedIndices();
  const sections = this.attr("sections");
  const sidenavContent = this.attr("loading") ? jsx(Loader, null) : sections.map((section, sectionIdx) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;
    const isExpanded = !this.state.collapsedSectionSet.has(section.label);
    const {
      itemIndex: selectedSidenavIndex
    } = allSelectedIndices.find(selectedIndices => selectedIndices.sectionIndex === sectionIdx) || {};
    const selectedItemInSection = selectedSidenavIndex > -1 ? sections[sectionIdx].items[selectedSidenavIndex] : null;
    return jsx(SidenavSection, {
      section: section,
      isExpanded: isExpanded,
      sectionDragDisabled: sectionDragDisabled,
      onToggleSection: this.helpers.toggleSection
    }, isExpanded && section.items.map((item, idx) => {
      var _a;

      return jsx(SidenavItemEl, {
        attrs: {
          item: Object.assign(Object.assign({}, item), {
            itemIdx: idx,
            sectionIdx
          }),
          'is-selected': selectedItemInSection && item.value === selectedItemInSection.value,
          'is-new': item.isNew && !((_a = this.state.hoveredItemValues) === null || _a === void 0 ? void 0 : _a.has(item.value)),
          'is-draggable': section.dragDropEnabled,
          'is-disabled': sectionDragDisabled
        },
        on: {
          itemDrop: this.helpers.onItemDrop,
          itemDragStart: this.helpers.onItemDragStart,
          itemDragEnd: this.helpers.onItemDragEnd,
          itemClick: this.helpers.onSelectItem,
          overflowMenuChange: this.helpers.onItemOverflowMenuChange,
          overflowMenuSelect: this.helpers.onItemOverflowMenuSelect,
          mouseenter: () => this.helpers.onItemMouseEntered(item)
        },
        props: {
          confirmNavigateFunc: this.confirmNavigateFunc
        }
      });
    }));
  });
  const header = this.attr("header");
  const footerActions = this.helpers.getFooterActions();
  return jsx("div", {
    sel: '.wrapper',
    class: {
      collapsed: this.state.isCollapsed,
      'is-hovering': this.state.isHovering || this.state.isOverflowMenuOpen,
      'is-resizing': this.state.isResizing
    },
    style: {
      '--sidenav-width': "".concat(this.state.width, "px")
    },
    on: {
      mouseleave: this.helpers.onSectionsMouseleave
    },
    attrs: {
      elref: "wrapper"
    }
  }, header ? jsx("div", {
    sel: '.header',
    class: {
      'is-scrolling': this.state.isScrolling && this.helpers.isExpandedStyle(),
      'vertical-header': "slot" in header
    }
  }, jsx("div", {
    sel: '.title'
  }, header.title), "slot" in header ? jsx("slot", {
    attrs: {
      name: this.state.isCollapsed && !this.state.isHovering ? header.collapsedSlot : header.slot
    }
  }) : jsx("div", {
    sel: '.header-cta'
  }, jsx(ButtonEl, {
    attrs: {
      theme: ButtonTheme.Tertiary,
      icon: header.cta.icon,
      elref: "headerCta"
    },
    on: {
      click: () => this.helpers.onHeaderActionClick(header.cta.value)
    }
  }), jsx(TooltipEl, {
    attrs: {
      placement: Placement.Right
    }
  }, header.cta.label))) : null, jsx("div", {
    sel: '.sections-container',
    on: {
      mouseenter: this.helpers.onSectionsMouseenter
    },
    hook: {
      insert: this.helpers.onSectionsInserted
    },
    attrs: {
      elref: "sectionsContainer"
    },
    class: {
      'is-overflow-menu-open': this.state.isOverflowMenuOpen
    }
  }, jsx("div", {
    sel: '.sections'
  }, sidenavContent)), (footerActions === null || footerActions === void 0 ? void 0 : footerActions.length) && jsx("div", {
    sel: '.footer',
    class: {
      'active-dragging-disabled': Number.isInteger(this.state.activeDragSectionIdx)
    }
  }, footerActions.map(cta => {
    return jsx("div", {
      sel: '.footer-cta.cta-item',
      on: {
        click: () => this.helpers.onFooterActionClick(cta.value),
        keypress: handleKeyPress
      },
      attrs: {
        value: cta.value,
        tabindex: 0
      }
    }, jsx(SvgIconEl, {
      attrs: {
        icon: cta.icon
      }
    }), jsx("div", {
      sel: '.footer-cta-label.cta-label'
    }, cta.label), !this.helpers.isExpandedStyle() && jsx(TooltipEl, {
      attrs: {
        placement: Placement.Right
      }
    }, cta.label));
  })), this.helpers.isExpandedStyle() && !this.state.isOverflowMenuOpen && jsx("div", {
    sel: '.resize-handle',
    on: {
      mousedown: this.helpers.startResizing
    },
    class: {
      'default-width': this.state.width === SIDENAV_DEFAULT_WIDTH
    },
    attrs: {
      elref: "resizeHandle"
    }
  }));
}